import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { BarChart3, TrendingUp, Users, DollarSign, Download } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useRevenue, useJobsMetrics, useWorkerMetrics, useDemand } from '@/hooks/data/useAnalytics'
import { downloadCsv } from '@/lib/csv'

const nzd = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' })

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function StatCard({ icon: Icon, label, value }: { icon: typeof BarChart3; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-50 p-2.5 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  )
}

function AnalyticsPage() {
  const [from, setFrom] = useState(() => isoDate(new Date(Date.now() - 29 * 86400000)))
  const [to, setTo] = useState(() => isoDate(new Date()))

  const revenue = useRevenue(from, to)
  const jobs = useJobsMetrics(from, to)
  const workers = useWorkerMetrics(from, to)
  const demand = useDemand(from, to)

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-6 h-6" /> Analytics
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Real event-projection metrics from AnalyticsService — empty until real bookings/jobs/quotes happen in this window.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="ml-2 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="ml-2 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </label>
        <button
          onClick={() => {
            if (revenue.data?.daily.length) downloadCsv(`revenue-${from}-to-${to}.csv`, revenue.data.daily)
            if (workers.data?.workers.length) downloadCsv(`workers-${from}-to-${to}.csv`, workers.data.workers)
            if (demand.data?.bySuburb.length) downloadCsv(`demand-${from}-to-${to}.csv`, demand.data.bySuburb)
          }}
          disabled={!revenue.data && !workers.data && !demand.data}
          className="ml-auto flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={DollarSign} label="Revenue" value={revenue.data ? nzd.format(revenue.data.total) : '—'} />
        <StatCard icon={TrendingUp} label="Payments" value={revenue.data ? String(revenue.data.payments) : '—'} />
        <StatCard icon={BarChart3} label="Jobs completed" value={jobs.data ? String(jobs.data.completed) : '—'} />
        <StatCard icon={Users} label="Quotes" value={demand.data ? String(demand.data.quotes) : '—'} />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Revenue by day</h2>
        {revenue.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !revenue.data || revenue.data.daily.length === 0 ? (
          <p className="text-sm text-gray-500">No revenue recorded in this window.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={revenue.data.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => nzd.format(Number(v))} />
              <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Worker performance</h2>
          {workers.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !workers.data || workers.data.workers.length === 0 ? (
            <p className="text-sm text-gray-500">No worker activity in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="pb-2">Worker</th>
                  <th className="pb-2">Jobs</th>
                  <th className="pb-2">Minutes</th>
                  <th className="pb-2">Check-ins</th>
                  <th className="pb-2">Geofence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {workers.data.workers.map((w) => (
                  <tr key={w.workerId}>
                    <td className="py-2 text-gray-500">{w.workerId.slice(0, 8)}…</td>
                    <td className="py-2">{w.jobsCompleted}</td>
                    <td className="py-2">{Math.round(w.minutesWorked)}</td>
                    <td className="py-2">{w.checkIns}</td>
                    <td className="py-2">{Math.round(w.geofenceCompliance * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Demand by suburb</h2>
          {demand.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !demand.data || demand.data.bySuburb.length === 0 ? (
            <p className="text-sm text-gray-500">No quotes in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="pb-2">Suburb</th>
                  <th className="pb-2">Quotes</th>
                  <th className="pb-2">Avg price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {demand.data.bySuburb.map((row) => (
                  <tr key={row.suburb}>
                    <td className="py-2 capitalize">{row.suburb}</td>
                    <td className="py-2">{row.quotes}</td>
                    <td className="py-2">{nzd.format(row.avgPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/analytics/')({ component: AnalyticsPage })
